import { useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { attachApiErrorContext, incrementUiMetric } from "../utils/sentry";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

/**
 * Hook that provides an authenticated API client with get, post, patch, delete methods.
 * Automatically attaches Bearer token from Auth0. Use inside Auth0Provider.
 * Return value is memoized to prevent unnecessary re-renders and effect loops.
 */
export function useApi() {
  const { getAccessTokenSilently } = useAuth0();

  return useMemo(() => {
    const request = async (path, options = {}) => {
      const token = await getAccessTokenSilently().catch(() => null);
      const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
      const headers = {
        ...(options.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const res = await fetch(url, { ...options, headers });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const apiPath = path.startsWith("http") ? new URL(path).pathname : path;
        attachApiErrorContext(errData, apiPath, res.status);
        incrementUiMetric("ui.api_client_error", 1, {
          status: String(res.status),
        });
        const err = new Error(
          errData.message || errData.error || `${res.status} ${res.statusText}`
        );
        err.status = res.status;
        err.data = errData;
        throw err;
      }
      const text = await res.text();
      if (!text || res.status === 204) return null;
      return JSON.parse(text);
    };

    return {
      get: (path) => request(path),
      post: (path, body) =>
        request(path, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
      patch: (path, body) =>
        request(path, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
      put: (path, body) =>
        request(path, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
      delete: (path) => request(path, { method: "DELETE" }),
    };
  }, [getAccessTokenSilently]);
}
